import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { ReactComponent as NoPollsIcon } from "../../Assets/iconsv2/generic/noPolls.svg";
import { ReactComponent as EditIcon } from "../../Assets/iconsv2/generic/editPencilIcon.svg";
import { ReactComponent as PlusIcon } from "../../Assets/icons/PlusIcon.svg";
import { ReactComponent as RadioIcon } from "../../Assets/iconsv2/generic/radioButtonIcon.svg";
import { ReactComponent as MultipleChoiceIcon } from "../../Assets/iconsv2/generic/checkBoxIcon.svg";
import { ReactComponent as DragIcon } from "../../Assets/iconsv2/generic/dragIcon.svg";
import { ReactComponent as DropDownArrow } from "../../Assets/icons/dropDownArrow.svg";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import PrimaryButtonRemove from "../GenericComponents/PrimaryButtonRemove";
import DraggableList, { useDragHandle } from "../GenericComponents/DraggableList";
import UnsavedChangesDialog from "../GenericComponents/UnsavedChangesDialog";
import { useNotify } from "../../Helpers/Notifications";
import { useConferencesService } from "../../Context/ConferenceContext";
import { usePollManagement } from "../../hooks/usePollManagement";
import PollOption from "./PollOption";
import { deepClone } from "../../Helpers/pollHelpers";
import {
  MAX_POLLS,
  MAX_OPTIONS,
  MIN_OPTIONS,
  QUESTION_MIN_LENGTH,
  QUESTION_MAX_LENGTH,
  OPTION_MAX_LENGTH,
  POLL_STATUS_DRAFT,
  ANSWER_TYPE_SINGLE,
  ANSWER_TYPE_MULTIPLE,
} from "../../Constants/constantValues";

const PollManagementModal = ({
  conferenceId = null,
  localPolls = [],
  setLocalPolls = () => {},
  onClose,
}) => {
  const { t } = useTranslate();
  const { notifyError, notifySuccess } = useNotify();
  const {
    getConferencePolls,
    createPollQuestion,
    updatePollQuestion,
    deletePollQuestion,
    createPollOption,
    updatePollOption,
    deletePollOption,
    reorderPollQuestions,
    reorderPollOptions,
  } = useConferencesService();

  const [loading, setLoading] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const isEditMode = conferenceId !== null;

  const pollManagement = usePollManagement({
    conferenceId,
    initialPolls: localPolls,
    isEditMode,
    MAX_POLLS,
    MAX_OPTIONS,
    MIN_OPTIONS,
    QUESTION_MIN_LENGTH,
    QUESTION_MAX_LENGTH,
    OPTION_MAX_LENGTH,
    conferencesService: {
      getConferencePolls,
      createPollQuestion,
      updatePollQuestion,
      deletePollQuestion,
      createPollOption,
      updatePollOption,
      deletePollOption,
      reorderPollQuestions,
      reorderPollOptions,
    },
  });

  const {
    polls,
    setPolls,
    setOriginalPolls,
    expandedPollId,
    setExpandedPollId,
    fieldErrors,
    questionInputRef,
    optionInputRefs,
    handleAddPoll,
    handleUpdateQuestionText,
    handleUpdateAnswerType,
    handleAddOption,
    handleUpdateOptionText,
    handleDeleteOption,
    validatePoll,
    handleDeletePoll,
    handleToggleExpand,
    handleReorderPolls,
    handleReorderOptions,
    isPollModified,
    getModifiedPolls,
  } = pollManagement;

  useEffect(() => {
    if (isEditMode) {
      loadPolls();
    } else {
      const clonedPolls = deepClone(localPolls);
      setPolls(clonedPolls);
      setOriginalPolls(deepClone(clonedPolls));
      if (clonedPolls.length > 0) {
        setExpandedPollId(clonedPolls[0].id);
      }
    }
  }, [conferenceId]);

  const loadPolls = () => {
    if (!conferenceId) return;
    setLoading(true);
    getConferencePolls(
      conferenceId,
      (response) => {
        const loadedPolls = response?.data?.polls || [];
        setPolls(loadedPolls);
        setOriginalPolls(deepClone(loadedPolls));
        setLoading(false);
        if (loadedPolls.length > 0) {
          setExpandedPollId(loadedPolls[0].id);
        }
      },
      () => {
        notifyError(t("general.something_wrong"));
        setLoading(false);
      }
    );
  };

  const handleSavePoll = (pollId) => {
    const poll = polls.find((p) => p.id === pollId);
    if (!poll) return;

    if (!validatePoll(poll)) {
      notifyError(t("conferences.fix_validation_errors"));
      return;
    }

    if (isEditMode) {
      savePollToBackend(poll);
    } else {
      const clonedPolls = deepClone(polls);
      setOriginalPolls(clonedPolls);
      setLocalPolls(clonedPolls);
      notifySuccess(t("conferences.poll_saved_locally"));
    }
  };

  const savePollToBackend = (poll) => {
    setLoading(true);

    const saveQuestion = () => {
      if (poll._isNew || String(poll.id).startsWith("temp-")) {
        const pollIndex = polls.findIndex(p => p.id === poll.id);
        createPollQuestion(
          conferenceId,
          poll.question_text,
          poll.answer_type,
          pollIndex >= 0 ? pollIndex : polls.length,
          (response) => {
            const newQuestion = response?.data?.poll;
            if (newQuestion) saveOptions(newQuestion);
          },
          () => {
            notifyError(t("general.something_wrong"));
            setLoading(false);
          }
        );
      } else {
        updatePollQuestion(
          poll.id,
          poll.question_text,
          poll.answer_type,
          (response) => {
            const updatedQuestion = response?.data?.poll;
            if (updatedQuestion) saveOptions(updatedQuestion);
          },
          () => {
            notifyError(t("general.something_wrong"));
            setLoading(false);
          }
        );
      }
    };

    const saveOptions = (question) => {
      const optionsToCreate = poll.options.filter(
        (opt) => opt._isNew || String(opt.id).startsWith("temp-")
      );
      const optionsToUpdate = poll.options.filter(
        (opt) => !opt._isNew && !String(opt.id).startsWith("temp-")
      );

      let completed = 0;
      const total = optionsToCreate.length + optionsToUpdate.length;

      const checkComplete = () => {
        completed++;
        if (completed === total) {
          notifySuccess(t("conferences.poll_saved"));
          loadPolls();
        }
      };

      optionsToCreate.forEach((opt, index) => {
        createPollOption(
          question.id,
          opt.option_text,
          opt.position !== undefined ? opt.position : index,
          () => checkComplete(),
          () => {
            notifyError(t("general.something_wrong"));
            setLoading(false);
          }
        );
      });

      optionsToUpdate.forEach((opt) => {
        updatePollOption(
          opt.id,
          opt.option_text,
          opt.position,
          () => checkComplete(),
          () => {
            notifyError(t("general.something_wrong"));
            setLoading(false);
          }
        );
      });

      if (total === 0) {
        notifySuccess(t("conferences.poll_saved"));
        loadPolls();
      }
    };

    saveQuestion();
  };

  const handleSaveAllAndClose = async () => {
    const modifiedPolls = getModifiedPolls();

    if (modifiedPolls.length === 0) {
      handleActualClose();
      return;
    }

    let hasErrors = false;
    for (const poll of modifiedPolls) {
      if (!validatePoll(poll)) {
      hasErrors = true;
    }
    }

    if (hasErrors) {
      notifyError(t("conferences.fix_validation_errors"));
      setShowUnsavedDialog(false);
      return;
    }

    if (isEditMode) {
      setLoading(true);

      const savePromises = modifiedPolls.map(poll => 
        new Promise((resolve, reject) => {
          const saveQuestion = () => {
            if (poll._isNew || String(poll.id).startsWith("temp-")) {
              const pollIndex = polls.findIndex(p => p.id === poll.id);
              createPollQuestion(
                conferenceId,
                poll.question_text,
                poll.answer_type,
                pollIndex >= 0 ? pollIndex : polls.length,
                (response) => {
                  const newQuestion = response?.data?.poll;
                  if (newQuestion) {
                    saveOptions(newQuestion, resolve, reject);
                  } else {
                    reject(new Error("No question returned"));
                  }
                },
                (error) => reject(error)
              );
            } else {
              updatePollQuestion(
                poll.id,
                poll.question_text,
                poll.answer_type,
                (response) => {
                  const updatedQuestion = response?.data?.poll;
                  if (updatedQuestion) {
                    saveOptions(updatedQuestion, resolve, reject);
                  } else {
                    reject(new Error("No question returned"));
                  }
                },
                (error) => reject(error)
              );
            }
          };

          const saveOptions = (question, resolve, reject) => {
            const optionsToCreate = poll.options.filter(
              (opt) => opt._isNew || String(opt.id).startsWith("temp-")
            );
            const optionsToUpdate = poll.options.filter(
              (opt) => !opt._isNew && !String(opt.id).startsWith("temp-")
            );

            const total = optionsToCreate.length + optionsToUpdate.length;
            
            if (total === 0) {
              resolve();
              return;
            }

            let completed = 0;
            let hasError = false;

            const checkComplete = () => {
              completed++;
              if (completed === total) {
                if (hasError) {
                  reject(new Error("Some options failed to save"));
                } else {
                  resolve();
                }
              }
            };

            optionsToCreate.forEach((opt, index) => {
              createPollOption(
                question.id,
                opt.option_text,
                opt.position !== undefined ? opt.position : index,
                () => checkComplete(),
                () => {
                  hasError = true;
                  checkComplete();
                }
              );
            });

            optionsToUpdate.forEach((opt) => {
              updatePollOption(
                opt.id,
                opt.option_text,
                opt.position,
                () => checkComplete(),
                () => {
                  hasError = true;
                  checkComplete();
                }
              );
            });
          };

          saveQuestion();
        })
      );

      Promise.all(savePromises)
        .then(() => {
        notifySuccess(t("conferences.poll_saved"));
        setLoading(false);
        setShowUnsavedDialog(false);
        handleActualClose();
      })
      .catch(() => {
        setLoading(false);
        setShowUnsavedDialog(false);
        notifyError(t("general.something_wrong"));
      });
    } else {
      setLocalPolls(polls);
      setShowUnsavedDialog(false);
      handleActualClose();
    }
  };

  const handleDiscardAndClose = () => {
    handleActualClose();
  };

  const handleActualClose = () => {
    setShowUnsavedDialog(false);
    onClose();
  };

  const handleCloseModal = () => {
    const modifiedPolls = getModifiedPolls();
    
    if (modifiedPolls.length > 0) {
      setShowUnsavedDialog(true);
      return;
    }

    handleActualClose();
  };

  const canEdit = (poll) => poll.status === POLL_STATUS_DRAFT;
  const modifiedPollsCount = getModifiedPolls().length;

  const DraggablePollHandle = ({ children, onClick }) => {
    const dragHandleProps = useDragHandle();
    
    return (
      <DragHandle 
        className="drag-handle" 
        {...dragHandleProps}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick(e);
        }}
      >
        {children}
      </DragHandle>
    );
  };

  const renderOption = (poll, option, optIndex, isDragging, isReadOnly, pollErrors) => {
    return (
      <PollOption
        key={option.id}
        poll={poll}
        option={option}
        optionIndex={optIndex}
        isDragging={isDragging}
        canEdit={!isReadOnly}
        hasError={!!pollErrors[option.id]}
        errorMessage={pollErrors[option.id]}
        onUpdateText={handleUpdateOptionText}
        onDelete={handleDeleteOption}
        optionInputRef={(el) => (optionInputRefs.current[option.id] = el)}
      />
    );
  };

  const renderPoll = (poll, index, isDragging) => {
    const isExpanded = expandedPollId === poll.id;
    const isReadOnly = !canEdit(poll);
    const pollErrors = fieldErrors[poll.id] || {};
    const hasUnsavedChanges = isPollModified(poll.id);

    return (
      <PollCard $isDragging={isDragging} $expanded={isExpanded}>
        <PollHeader onClick={() => handleToggleExpand(poll.id)}>
          <DraggablePollHandle>
            <DragIcon width={18} height={18} />
            <PollNumber>#{index + 1}</PollNumber>
          </DraggablePollHandle>
          <PollQuestionPreview>
            {poll.question_text || t("conferences.untitled_poll")}
          </PollQuestionPreview>
          <PollStatus $status={poll.status}>
            {t(`conferences.poll_status_${poll.status}`)}
          </PollStatus>
          <ExpandIconWrapper $expanded={isExpanded}>
            <DropDownArrow width={12} height={12} />
          </ExpandIconWrapper>
        </PollHeader>

        {isExpanded && (
          <PollBody>
            <Group>
              <SubtitleRow>
                <Subtitle>{t("conferences.question")}</Subtitle>
                <CharCount $warning={poll.question_text?.length > QUESTION_MAX_LENGTH * 0.9}>
                  {poll.question_text?.length || 0}/{QUESTION_MAX_LENGTH}
                </CharCount>
              </SubtitleRow>
              <QuestionInput
                ref={poll._isNew ? questionInputRef : null}
                value={poll.question_text}
                onChange={(e) => handleUpdateQuestionText(poll.id, e.target.value)}
                placeholder={t("conferences.enter_question")}
                maxLength={QUESTION_MAX_LENGTH}
                disabled={isReadOnly}
                readOnly={isReadOnly}
                $hasError={!!pollErrors.question}
              />
              {pollErrors.question && (
                <FieldError>{pollErrors.question}</FieldError>
              )}
            </Group>

            <Group>
              <Subtitle>{t("conferences.answer_type")}</Subtitle>
              <ToggleWrapper>
                <ToggleOption
                  type="button"
                  $active={poll.answer_type === ANSWER_TYPE_SINGLE}
                  $position="left"
                  onClick={() => !isReadOnly && handleUpdateAnswerType(poll.id, ANSWER_TYPE_SINGLE)}
                  disabled={isReadOnly}
                >
                  <ToggleIconWrapper>
                    <RadioIcon width={16} height={16} />
                  </ToggleIconWrapper>
                  {t("conferences.single_choice")}
                </ToggleOption>
                <ToggleOption
                  type="button"
                  $active={poll.answer_type === ANSWER_TYPE_MULTIPLE}
                  $position="right"
                  onClick={() => !isReadOnly && handleUpdateAnswerType(poll.id, ANSWER_TYPE_MULTIPLE)}
                  disabled={isReadOnly}
                >
                  <ToggleIconWrapper>
                    <MultipleChoiceIcon width={16} height={16} />
                  </ToggleIconWrapper>
                  {t("conferences.multiple_choice")}
                </ToggleOption>
              </ToggleWrapper>
            </Group>

            <Group>
              <SubtitleRow>
                <Subtitle>
                  {t("conferences.options")} ({poll.options.length}/{MAX_OPTIONS})
                </Subtitle>
                {!isReadOnly && (
                  <AddOptionButton
                    type="button"
                    onClick={() => handleAddOption(poll.id)}
                    disabled={poll.options.length >= MAX_OPTIONS}
                  >
                    <PlusIcon width={14} height={14} />
                    {t("conferences.add_option")}
                  </AddOptionButton>
                )}
              </SubtitleRow>

              <DraggableList
                items={poll.options}
                onReorder={(newOptions) => handleReorderOptions(poll.id, newOptions)}
                disabled={isReadOnly}
                renderItem={(option, optIndex, isOptionDragging) =>
                  renderOption(poll, option, optIndex, isOptionDragging, isReadOnly, pollErrors)
                }
              />
            </Group>

            <PollActions>
              {!isReadOnly && (
                <>
                  <PrimaryButton
                    onClick={() => handleSavePoll(poll.id)}
                    disabled={loading || !hasUnsavedChanges}
                  >
                    {t("general.save") + " " + t("conferences.poll")}
                  </PrimaryButton>
                  <PrimaryButtonRemove
                    onClick={() => handleDeletePoll(poll.id)}
                    disabled={loading}
                  >
                    {t("general.delete")  + " " + t("conferences.poll")}
                  </PrimaryButtonRemove>
                </>
              )}
              {isReadOnly && (
                <InfoText>{t("conferences.cannot_edit_active_poll")}</InfoText>
              )}
            </PollActions>
          </PollBody>
        )}
      </PollCard>
    );
  };

  return (
    <FullScreenItem>
      <Wrapper>
        <Header>
          <Title>{t("conferences.manage_polls")}</Title>
          <CloseBtn onClick={handleCloseModal}>
            <CloseIcon />
          </CloseBtn>
        </Header>

        <CardInputsWrapper>
          {polls.length === 0 && (
            <EmptyState>
              <NoPollsIcon width={480} opacity={0.8} />
              <EmptyText>{t("conferences.no_polls_created")}</EmptyText>
              <EmptySubtext>Click "Add New Poll" below to get started</EmptySubtext>
            </EmptyState>
          )}

          <DraggableList
            items={polls}
            onReorder={handleReorderPolls}
            renderItem={renderPoll}
          />

          {polls.length < MAX_POLLS && (
            <AddPollButton type="button" onClick={handleAddPoll}>
              <EditIcon width={18} height={18} />
              {t("conferences.add_new_poll")}
            </AddPollButton>
          )}
        </CardInputsWrapper>

        <CloseModalButton
          onClick={handleCloseModal}
          disabled={loading}
          $hasChanges={modifiedPollsCount > 0}
        >
          {modifiedPollsCount > 0 ? t("conferences.save_and_close") : t("general.close")}
        </CloseModalButton>
      </Wrapper>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        onSave={handleSaveAllAndClose}
        onDiscard={handleDiscardAndClose}
      />
    </FullScreenItem>
  );
};

export default PollManagementModal;

const FullScreenItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  height: 90%;
  width: 100%;
  max-width: 750px;
  background: ${({ theme }) => theme.menu.background};
  gap: 20px;
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const Title = styled.h2`
  text-transform: capitalize;
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  svg {
    height: 28px;
    width: 28px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;

const CardInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.menu.groupContentBackground};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.sidebar.borderColor};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 12px;
`;

const EmptyText = styled.p`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const EmptySubtext = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  margin: 0;
`;

const PollCard = styled.div`
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 8px;
  background: ${({ theme, $isDragging }) =>
    $isDragging
      ? theme.menu.selectedOpionBackground
      : theme.menu.groupContentBackground};
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: ${({ $isDragging }) =>
    $isDragging ? "0 8px 16px rgba(0, 0, 0, 0.4)" : "none"};
`;

const PollHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${({ theme }) => theme.menu.background};
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: grab;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  svg {
    opacity: 0.4;
    transition: opacity 0.2s ease;
    color: ${({ theme }) => theme.menu.primaryTextColor}
  }
  
  &:hover {
    background: ${({ theme }) => theme.menu.groupContentBackground};
    
    svg {
      opacity: 0.7;
    }
  }
  
  &:active {
    cursor: grabbing;
    background: ${({ theme }) => theme.sidebar.borderColor}30;
    
    svg {
      opacity: 1;
    }
  }
`;

const PollNumber = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 600;
  font-size: 13px;
  min-width: 30px;
`;

const PollQuestionPreview = styled.span`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-weight: 500;
  font-size: 14px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PollStatus = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme, $status }) => {
    if ($status === "launched") return "#FFFFFF";
    if ($status === "ended") return "#FFFFFF";
    return theme.menu.secondaryTextColor;
  }};
  background: ${({ theme, $status }) => {
    if ($status === "launched") return theme.valid_green;
    if ($status === "ended") return theme.menu.secondaryTextColor;
    return theme.menu.background;
  }};
  border: ${({ theme, $status }) => 
    $status === "draft" ? `1px solid ${theme.sidebar.borderColor}` : "none"
  };
`;

const ExpandIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.2s ease;
  margin-left: 8px;
  
  svg {
    fill: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const PollBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.background};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`;

const SubtitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CharCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme, $warning }) =>
    $warning ? theme.menu.deleteColor : theme.menu.secondaryTextColor};
`;

const QuestionInput = styled.textarea`
  width: 100%;
  min-height: 50px;
  padding: 14px;
  border: 2px solid ${({ theme, $hasError }) =>
    $hasError ? theme.menu.deleteColor : theme.sidebar.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  resize: vertical;
  opacity: ${({ readOnly }) => (readOnly ? 0.6 : 1)};
  cursor: ${({ readOnly }) => (readOnly ? "default" : "text")};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.menu.deleteColor : theme.menu.selectedTextColor};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    opacity: 0.6;
  }
`;

const FieldError = styled.div`
  color: ${({ theme }) => theme.menu.deleteColor};
  font-size: 12px;
  font-weight: 500;
  margin-top: -8px;
  padding-left: 4px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  border: 2px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.menu.groupContentBackground};
`;

const ToggleIconWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-right: 6px;
  
  svg {
    fill: currentColor;
  }
`;

const ToggleOption = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: ${({ theme, $active }) =>
    $active ? theme.menu.selectedTextColor : "transparent"};
  color: ${({ theme, $active }) =>
    $active ? "#FFFFFF" : theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${({ disabled, $active }) => (disabled ? 0.5 : $active ? 1 : 0.8)};
  border-right: ${({ $position, theme }) =>
    $position === "left" ? `2px solid ${theme.sidebar.borderColor}` : "none"};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.menu.selectedTextColor : theme.menu.background};
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const AddOptionButton = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

const PollActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 13px;
  margin: 0;
  font-style: italic;
`;

const AddPollButton = styled.button`
  width: 100%;
  padding: 18px;
  border: 2px dashed ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    fill: ${({ theme }) => theme.menu.selectedTextColor};
    transition: transform 0.2s ease;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
    background: ${({ theme }) => theme.menu.selectedTextColor}15;
    border-style: solid;
  }
  
  &:active {
    transform: scale(0.99);
  }
`;

const CloseModalButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.menu.selectedTextColor};
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;